import type { Client } from '@rendimees24/shared-types';
import {
  collection,
  doc,
  getDoc,
  onSnapshot,
  orderBy,
  query,
  type DocumentData,
  type QuerySnapshot,
} from 'firebase/firestore';

import {
  searchCompaniesDirect,
  type AriregisterCompany,
} from './ariregister';
import { httpsCallableWithLog as httpsCallable } from './errorLog';
import { getFirebase } from './firebase';

export type { AriregisterCompany } from './ariregister';

export interface CreateClientInput {
  registrationNumber: string;
  companyName: string;
  vatNumber?: string;
  countryCode: 'EE' | 'FI' | 'LV' | 'LT';
  contactEmail: string;
  contactPhone: string;
  billingAddress: string;
  // Optional: if the browser already fetched an Ariregister snapshot,
  // pass it so the Cloud Function doesn't have to repeat the lookup
  // (it will fail anyway because Google Cloud IPs are blocked).
  ariregisterSnapshot?: {
    fetchedAt: string;
    legalName: string;
    statusCode?: string;
    statusText?: string;
    addressFormatted?: string;
  };
}

function fromFirestore(snap: DocumentData): Client {
  const data = snap.data() ?? {};
  return { id: snap.id, ...data } as Client;
}

/**
 * Browser-direct Ariregister search. See services/ariregister.ts for why this
 * doesn't go through a Cloud Function today.
 */
export async function searchEstonianCompanies(q: string): Promise<AriregisterCompany[]> {
  return searchCompaniesDirect(q);
}

export async function createClient(input: CreateClientInput): Promise<string> {
  const { functions } = getFirebase();
  const callable = httpsCallable<CreateClientInput, { id: string }>(functions, 'createClient');
  const { data } = await callable(input);
  return data.id;
}

export interface CreateClientUserInput {
  clientId: string;
  email: string;
  firstName: string;
  lastName: string;
  phone?: string;
  language?: 'et' | 'en' | 'ru' | 'fi';
  primaryContact?: boolean;
}

export interface CreateClientUserResult {
  uid: string;
  linkId: string;
  resetLink: string;
  /** Only a message actually handed to SMTP counts. */
  emailSent?: boolean;
  delivery?: 'delivered' | 'captured' | 'failed';
  emailError?: string;
  /** EPIC 54 §6.2 — when it was sent, when it lapses, what became of it. */
  invitation?: {
    outcome: 'prepared' | 'sent' | 'send_failed' | 'accepted' | 'expired' | 'cancelled';
    sentAt?: string;
    expiresAt?: string;
    sendCount?: number;
    error?: string;
  };
}

export interface SelfSignupClientInput {
  firstName: string;
  lastName: string;
  phone: string;
  language?: 'et' | 'en' | 'ru' | 'fi';
  registrationNumber: string;
  companyName: string;
  vatNumber?: string;
  countryCode?: 'EE' | 'FI' | 'LV' | 'LT';
  contactEmail: string;
  contactPhone: string;
  billingAddress: string;
  ariregisterSnapshot?: CreateClientInput['ariregisterSnapshot'];
}

export interface SelfSignupClientResult {
  clientId: string;
  clientStatus: 'pending' | 'approved' | 'active' | 'suspended' | 'churned';
  isNewClient: boolean;
}

export async function selfSignupClient(
  input: SelfSignupClientInput,
): Promise<SelfSignupClientResult> {
  const { functions } = getFirebase();
  const callable = httpsCallable<SelfSignupClientInput, SelfSignupClientResult>(
    functions,
    'selfSignupClient',
  );
  const { data } = await callable(input);
  return data;
}

export interface SetClientPaymentTermsInput {
  clientId: string;
  paymentTermType: 'net_days' | 'prepay';
  paymentTermDays: 0 | 7 | 14 | 21 | 25 | 30;
  creditLimitEur?: number;
}

/** EPIC 27 — admin/finance sets payment terms from the client card (locks override). */
export async function setClientPaymentTerms(
  input: SetClientPaymentTermsInput,
): Promise<{ ok: true; paymentTermType: string; paymentTermDays: number }> {
  const { functions } = getFirebase();
  const callable = httpsCallable<
    SetClientPaymentTermsInput,
    { ok: true; paymentTermType: string; paymentTermDays: number }
  >(functions, 'setClientPaymentTerms');
  const { data } = await callable(input);
  return data;
}

export interface SetClientContractStatusInput {
  clientId: string;
  isContract: boolean;
  startDate?: string;
  endDate?: string;
}

/** EPIC 30 — admin/finance marks a client as a contract client (lepinguline klient) or removes the mark. */
export async function setClientContractStatus(
  input: SetClientContractStatusInput,
): Promise<{ ok: true; isContract: boolean }> {
  const { functions } = getFirebase();
  const callable = httpsCallable<SetClientContractStatusInput, { ok: true; isContract: boolean }>(
    functions,
    'setClientContractStatus',
  );
  const { data } = await callable(input);
  return data;
}

/** EPIC 37 — client applies (from the portal) to become a contract client. */
export async function requestContractStatus(input: {
  clientId: string;
  note?: string;
  expectedMonthlyHours?: number;
}): Promise<{ ok: true }> {
  const { functions } = getFirebase();
  const callable = httpsCallable<typeof input, { ok: true }>(functions, 'requestContractStatus');
  return (await callable(input)).data;
}

/** EPIC 37 — admin declines a contract application (approval goes via setClientContractStatus). */
export async function setContractRequestDecision(input: {
  clientId: string;
  declineReason: string;
}): Promise<{ ok: true }> {
  const { functions } = getFirebase();
  const callable = httpsCallable<typeof input, { ok: true }>(functions, 'setContractRequestDecision');
  return (await callable(input)).data;
}

export async function createClientUser(
  input: CreateClientUserInput,
): Promise<CreateClientUserResult> {
  const { functions } = getFirebase();
  const callable = httpsCallable<CreateClientUserInput, CreateClientUserResult>(
    functions,
    'createClientUser',
  );
  const { data } = await callable(input);
  return data;
}

export interface SetClientStatusInput {
  clientId: string;
  status: 'active' | 'approved' | 'suspended' | 'churned';
  reason?: string;
}

/** Approve / suspend / reactivate a client (admin/dispatcher). */
export async function setClientStatus(
  input: SetClientStatusInput,
): Promise<{ ok: true; status: string; emailed: boolean }> {
  const { functions } = getFirebase();
  const callable = httpsCallable<SetClientStatusInput, { ok: true; status: string; emailed: boolean }>(
    functions,
    'setClientStatus',
  );
  return (await callable(input)).data;
}

/** Hard-delete a client with no jobs/invoices (admin). */
export async function deleteClient(clientId: string): Promise<{ ok: true }> {
  const { functions } = getFirebase();
  const callable = httpsCallable<{ clientId: string }, { ok: true }>(functions, 'deleteClient');
  return (await callable({ clientId })).data;
}

export interface PurgeIndividualClientsResult {
  dryRun: boolean;
  clients: number;
  jobs: number;
  invoices: number;
  links: number;
}

/** DEV: delete all individual (eraisik) clients + their data. dryRun reports counts only. */
export async function purgeIndividualClients(
  dryRun: boolean,
): Promise<PurgeIndividualClientsResult> {
  const { functions } = getFirebase();
  const callable = httpsCallable<{ dryRun: boolean }, PurgeIndividualClientsResult>(
    functions,
    'purgeIndividualClients',
  );
  return (await callable({ dryRun })).data;
}

export async function fetchClient(id: string): Promise<Client | null> {
  const { firestore } = getFirebase();
  const snap = await getDoc(doc(firestore, 'clients', id));
  if (!snap.exists()) return null;
  return fromFirestore(snap);
}

export function subscribeClients(onSnap: (clients: Client[]) => void): () => void {
  const { firestore } = getFirebase();
  const q = query(collection(firestore, 'clients'), orderBy('createdAt', 'desc'));
  return onSnapshot(q, (snap: QuerySnapshot) => {
    onSnap(snap.docs.map((d) => ({ id: d.id, ...d.data() }) as Client));
  });
}

export interface ClientPaymentTerms {
  ok: true;
  mode: 'prepay' | 'net_days';
  /** Days after completion the invoice falls due; 0 when prepaying. */
  days: number;
  /** Earliest start the server accepts (YYYY-MM-DD) — mirrors its own check. */
  minStartDate: string;
  minLeadDays: number;
}

/**
 * THE payment terms for a client, decided server-side.
 *
 * The order form used to infer this from `client.pricing.paymentTermType`,
 * which is one input out of eight — so the form and the server disagreed and
 * the client was shown prepayment rules for an order that turned out to be on
 * net terms (and, worse, no prepayment warning for orders that turned out to
 * need one). Ask, never guess.
 */
export async function getClientPaymentTerms(clientId: string): Promise<ClientPaymentTerms> {
  const { functions } = getFirebase();
  const callable = httpsCallable<{ clientId: string }, ClientPaymentTerms>(
    functions,
    'getClientPaymentTerms',
  );
  return (await callable({ clientId })).data;
}
